import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import {
  Currency,
  Sport,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, Title2, Title3 } from '@sorare/core/src/atoms/typography';
import { DumbCardBack } from '@sorare/core/src/components/card/Back/DumbCardBack';
import CardFront from '@sorare/core/src/components/card/Front';
import { BackBoxReward } from '@sorare/core/src/components/rewards/BoxReward/Back';
import { FrontBoxReward } from '@sorare/core/src/components/rewards/BoxReward/Front';
import { BackCoinReward } from '@sorare/core/src/components/rewards/CoinReward/Back';
import { FrontCoinReward } from '@sorare/core/src/components/rewards/CoinReward/Front';
import { BackConversionCreditReward } from '@sorare/core/src/components/rewards/ConversionCreditReward/Back';
import { FrontConversionCreditReward } from '@sorare/core/src/components/rewards/ConversionCreditReward/Front';
import { BackMonetaryReward } from '@sorare/core/src/components/rewards/MonetaryReward/BackMonetaryReward';
import { FrontMonetaryReward } from '@sorare/core/src/components/rewards/MonetaryReward/FrontMonetaryReward';
import { Reward } from '@sorare/core/src/components/rewards/types';
import {
  ANY_SPORT_CARD_SHOW,
  ANY_SPORT_CLUB_SHOP,
} from '@sorare/core/src/constants/routes';
import { getAnyPositionInitials } from '@sorare/core/src/lib/getAnyPositionInitials';
import { isType, withFragments } from '@sorare/core/src/lib/gql';
import {
  MonetaryAmountCurrency,
  monetaryAmountFragment,
} from '@sorare/core/src/lib/monetaryAmount';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import DeliverableItemFooter from 'components/rewards/deliverableItem/DeliverableItemFooter';
import JerseyRewardCard from 'components/rewards/deliverableItem/DeliverableItemFooter/jersey/JerseyRewardCard';
import jerseyBack from 'components/rewards/deliverableItem/DeliverableItemFooter/jersey/assets/jersey_back.png';
import ExperienceRewardCard from 'components/rewards/deliverableItem/ExperienceRewardCard';
import experienceBack from 'components/rewards/deliverableItem/ExperienceRewardCard/experience_back.png';
import TicketsRewardCard from 'components/rewards/deliverableItem/TicketsRewardCard';
import ticketsBack from 'components/rewards/deliverableItem/TicketsRewardCard/tickets_back.png';

import { RewardHeader } from './Header';
import {
  formatBoxRewards_so5Reward,
  formatCardRewards_so5Reward,
  formatCoinRewards_so5Reward,
  formatConversionCreditRewards_so5Reward,
  formatDeliverableItemRewards_so5Reward,
  formatMonetaryRewards_so5Reward,
  formatRewardHeader_so5Reward,
  formatReward_so5Reward,
} from './__generated__/utils.graphql';

const CoinRewardHeader = styled(Vertical).attrs({ gap: 3, center: true })`
  padding-top: var(--triple-unit);
  height: 100%;
`;
const ClickableButtonLink = styled(Button)`
  /* Override the rewards banner dialog header value (pointer-events: none) */
  pointer-events: all;
`;
const DeliverableItemFooterWrapper = styled.div`
  padding: var(--double-unit);
`;

const formatCoinRewards = withFragments(
  (
    coinRewards: formatCoinRewards_so5Reward[],
    { sport }: { sport?: Sport }
  ): Reward | null => {
    if (!coinRewards.length) return null;

    const totalCoinAmount = coinRewards.reduce(
      (acc, reward) => acc + reward.coinAmount,
      0
    );

    return {
      ids: coinRewards.map(reward => reward.id),
      key: totalCoinAmount.toString(),
      backgroundText: 'Coins',
      back: <BackCoinReward />,
      front: <FrontCoinReward amount={totalCoinAmount} />,
      teasers: [],
      header: (
        <CoinRewardHeader>
          <Title2 color="var(--c-static-neutral-100)">
            <FormattedMessage
              id="rewards.Header.Coins"
              defaultMessage="Coins"
            />
          </Title2>
          <LabelM>
            <FormattedMessage
              id="rewards.Header.Coins.Subtitle"
              defaultMessage="Spend coins on a variety of rewards"
            />
          </LabelM>
          <ClickableButtonLink
            to={generateSportPath(ANY_SPORT_CLUB_SHOP, { sport })}
            color="secondary"
            size="small"
          >
            <FormattedMessage
              id="rewards.Header.Coins.Cta"
              defaultMessage="Go to Club Shop"
            />
          </ClickableButtonLink>
        </CoinRewardHeader>
      ),
      claimed: coinRewards.every(reward => reward.aasmState === 'claimed'),
    };
  },
  {
    so5Reward: gql`
      fragment formatCoinRewards_so5Reward on So5Reward {
        id
        slug
        aasmState
        coinAmount
      }
    ` as TypedDocumentNode<formatCoinRewards_so5Reward>,
  }
);

const formatRewardHeader = withFragments(
  (reward: formatRewardHeader_so5Reward): ReactElement => {
    const { so5Fixture, so5Ranking, so5UserGroupMembership } = reward;

    const { shortDisplayName } = so5Fixture || {};
    const { score, ranking } = so5Ranking || so5UserGroupMembership || {};
    const title =
      so5Ranking?.so5Lineup?.so5Leaderboard?.displayName ||
      so5UserGroupMembership?.so5UserGroup?.displayName ||
      '';

    return (
      <RewardHeader
        title={title}
        rank={ranking || 0}
        points={score || 0}
        gameWeek={shortDisplayName}
        iconUrl={so5Ranking?.so5Lineup?.so5Leaderboard?.iconUrl}
      />
    );
  },
  {
    so5Reward: gql`
      fragment formatRewardHeader_so5Reward on So5Reward {
        id
        slug
        so5Ranking {
          id
          so5Lineup {
            id
            so5Leaderboard {
              slug
              iconUrl
              displayName
            }
          }
          ranking
          score
        }
        so5UserGroupMembership {
          id
          ranking
          score
          so5UserGroup {
            slug
            id
            displayName
          }
        }
        so5Fixture {
          slug
          shortDisplayName
        }
      }
    ` as TypedDocumentNode<formatRewardHeader_so5Reward>,
  }
);

const formatMonetaryRewards = withFragments(
  (
    monetaryRewards: formatMonetaryRewards_so5Reward[],
    { onClaimFiatRewards }: { onClaimFiatRewards?: () => void } = {}
  ): Reward[] => {
    if (!monetaryRewards.length) return [];

    return monetaryRewards
      .map(reward => {
        const { id, aasmState, amount } = reward;

        if (
          !amount ||
          BigInt(
            amount[
              amount.referenceCurrency.toLowerCase() as MonetaryAmountCurrency
            ] || 0
          ) === 0n
        )
          return null;

        const currency =
          amount?.referenceCurrency === SupportedCurrency.WEI
            ? Currency.ETH
            : Currency.FIAT;

        return {
          ids: [id],
          key: id,
          backgroundText: 'Cash',
          back: <BackMonetaryReward currency={currency} />,
          front: <FrontMonetaryReward monetaryAmount={amount} />,
          teasers: [],
          header: formatRewardHeader(reward),
          claimed: aasmState === 'claimed',
          ...(currency === Currency.FIAT && onClaimFiatRewards
            ? { onClick: () => onClaimFiatRewards() }
            : {}),
        };
      })
      .filter(Boolean);
  },
  {
    so5Reward: gql`
      fragment formatMonetaryRewards_so5Reward on So5Reward {
        id
        slug
        aasmState
        amount {
          ...MonetaryAmountFragment_monetaryAmount
        }
        ...formatRewardHeader_so5Reward
      }
      ${monetaryAmountFragment}
      ${formatRewardHeader.fragments.so5Reward}
    ` as TypedDocumentNode<formatMonetaryRewards_so5Reward>,
  }
);

const formatCardRewards = withFragments(
  (
    cardRewards: formatCardRewards_so5Reward[],
    {
      sport,
      sendUserEngagementIncrementByTier,
    }: {
      sport?: Sport;
      sendUserEngagementIncrementByTier?: (tier: number) => void;
    }
  ): Reward[] => {
    if (!cardRewards.length) return [];
    return cardRewards
      .map(reward => {
        const { id, aasmState, rewardCards } = reward;

        return rewardCards
          .map(
            ({ id: rewardCardId, anyCard: card, backPictureUrl, quality }) => {
              if (!card) return null;

              const { pictureUrl, anyPlayer } = card;

              return {
                ids: [id],
                key: rewardCardId,
                backgroundText: 'Card',
                back: (
                  <>
                    {backPictureUrl && (
                      <DumbCardBack path={backPictureUrl} aria-label="" />
                    )}
                  </>
                ),
                front: (
                  <>
                    {pictureUrl && (
                      <CardFront src={pictureUrl} alt={anyPlayer.displayName} />
                    )}
                  </>
                ),
                teasers: [
                  sport === Sport.FOOTBALL && anyPlayer?.country.flagUrl && (
                    <img key={0} src={anyPlayer?.country.flagUrl} alt="" />
                  ),
                  <Title3 key={1}>{getAnyPositionInitials(anyPlayer)}</Title3>,
                  anyPlayer?.activeClub?.pictureUrl && (
                    <img
                      key={2}
                      src={anyPlayer?.activeClub?.pictureUrl}
                      alt=""
                    />
                  ),
                ].filter(Boolean),
                header: formatRewardHeader(reward),
                claimed: aasmState === 'claimed',
                handleClaimEvent: () => {
                  if (quality) {
                    const tierAsNumber = +quality.replace('tier_', '');
                    sendUserEngagementIncrementByTier?.(tierAsNumber);
                  }
                },
                footer: (claimed?: boolean, revealing?: boolean) =>
                  claimed &&
                  !revealing && (
                    <Link
                      to={generateSportPath(ANY_SPORT_CARD_SHOW, {
                        params: {
                          slug: card.slug,
                        },
                        sport,
                      })}
                    >
                      <Button color="primary" size="small">
                        <FormattedMessage
                          id="rewards.Footer.cardPageLink"
                          defaultMessage="Check your card"
                        />
                      </Button>
                    </Link>
                  ),
              };
            }
          )
          .filter(Boolean);
      })
      .flat();
  },
  {
    so5Reward: gql`
      fragment formatCardRewards_so5Reward on So5Reward {
        id
        slug
        aasmState
        rewardCards {
          id
          quality
          anyCard {
            slug
            rarityTyped
            anyPlayer {
              slug
              displayName
              age
              ...getAnyPositionInitials_AnyPlayerInterface
              country {
                slug
                flagUrl(size: 32)
              }
              activeClub {
                slug
                pictureUrl
              }
            }
            pictureUrl
          }
          backPictureUrl
        }
        ...formatRewardHeader_so5Reward
      }
      ${formatRewardHeader.fragments.so5Reward}
      ${getAnyPositionInitials.fragments.AnyPlayerInterface}
    ` as TypedDocumentNode<formatCardRewards_so5Reward>,
  }
);
const formatBoxRewards = withFragments(
  (boxRewards: formatBoxRewards_so5Reward[]): Reward[] => {
    if (!boxRewards.length) return [];

    return boxRewards
      .map(boxReward => {
        const { id: rewardId, aasmState, probabilisticBundle } = boxReward;

        if (!probabilisticBundle) {
          return [];
        }
        return {
          ids: [rewardId],
          key: rewardId,
          backgroundText: 'Reward Box',
          back: <BackBoxReward />,
          front: <FrontBoxReward box={probabilisticBundle.config} />,
          teasers: [],
          header: formatRewardHeader(boxReward),
          claimed: aasmState === 'claimed',
        };
      })
      .flat();
  },
  {
    so5Reward: gql`
      fragment formatBoxRewards_so5Reward on So5Reward {
        id
        slug
        aasmState
        probabilisticBundle {
          config {
            id
            pictureUrl
            name
          }
          id
        }
        ...formatRewardHeader_so5Reward
      }
      ${formatRewardHeader.fragments.so5Reward}
    ` as TypedDocumentNode<formatBoxRewards_so5Reward>,
  }
);

export const formatConversionCreditRewards = withFragments(
  (conversionCredits: formatConversionCreditRewards_so5Reward[]): Reward[] => {
    if (!conversionCredits.length) return [];

    return conversionCredits
      .map(conversionCreditReward => {
        const {
          id: rewardId,
          aasmState,
          conversionCredit,
        } = conversionCreditReward;

        if (!conversionCredit) {
          return [];
        }

        return {
          ids: [rewardId],
          key: rewardId,
          backgroundText: 'CONVERSION CREDIT',
          back: <BackConversionCreditReward />,
          front: (
            <FrontConversionCreditReward
              monetaryAmount={conversionCredit.maxDiscount}
            />
          ),
          teasers: [],
          header: formatRewardHeader(conversionCreditReward),
          claimed: aasmState === 'claimed',
        };
      })
      .flat();
  },
  {
    so5Reward: gql`
      fragment formatConversionCreditRewards_so5Reward on So5Reward {
        id
        slug
        aasmState
        conversionCredit {
          maxDiscount {
            ...MonetaryAmountFragment_monetaryAmount
          }
        }
        ...formatRewardHeader_so5Reward
      }
      ${formatRewardHeader.fragments.so5Reward}
      ${monetaryAmountFragment}
    ` as TypedDocumentNode<formatConversionCreditRewards_so5Reward>,
  }
);

const formatDeliverableItemRewards = withFragments(
  (
    deliverableItemRewards: formatDeliverableItemRewards_so5Reward[],
    {
      safeGoNextReward,
    }: {
      safeGoNextReward?: () => void;
    }
  ): Reward[] => {
    if (!deliverableItemRewards.length) return [];

    return deliverableItemRewards
      .map(deliverableItemReward => {
        const { id, aasmState, deliverableItems } = deliverableItemReward;
        if (!deliverableItems) return null;
        const { deliverableItem } = deliverableItems[0];
        if (!deliverableItem) return null;

        const defaultDeliverableItem = {
          ids: [id],
          key: id,
          header: formatRewardHeader(deliverableItemReward),
          footer: () => (
            <DeliverableItemFooterWrapper>
              <DeliverableItemFooter
                deliverableItem={deliverableItem}
                onSkip={safeGoNextReward}
              />
            </DeliverableItemFooterWrapper>
          ),
          claimed: aasmState === 'claimed',
          teasers: [],
        };

        if (isType(deliverableItem, 'JerseyDeliverableItem')) {
          return {
            ...defaultDeliverableItem,
            backgroundText: 'Jersey',
            back: <DumbCardBack path={jerseyBack} aria-label="" />,
            front: <JerseyRewardCard jersey={deliverableItem} />,
            teasers: [
              deliverableItem.competition?.pictureUrl && (
                <img
                  key={0}
                  src={deliverableItem.competition.pictureUrl}
                  alt=""
                />
              ),
              deliverableItem.team?.pictureUrl && (
                <img key={0} src={deliverableItem.team.pictureUrl} alt="" />
              ),
            ].filter(Boolean),
          };
        }
        if (isType(deliverableItem, 'ExperienceDeliverableItem')) {
          return {
            ...defaultDeliverableItem,
            backgroundText: 'Experience',
            back: <DumbCardBack path={experienceBack} aria-label="" />,
            front: <ExperienceRewardCard experience={deliverableItem} />,
          };
        }
        if (isType(deliverableItem, 'TicketsDeliverableItem')) {
          return {
            ...defaultDeliverableItem,
            backgroundText: 'Tickets',
            back: <DumbCardBack path={ticketsBack} aria-label="" />,
            front: <TicketsRewardCard ticket={deliverableItem} />,
          };
        }
        return null;
      })
      .filter(Boolean);
  },
  {
    so5Reward: gql`
      fragment formatDeliverableItemRewards_so5Reward on So5Reward {
        id
        slug
        aasmState
        deliverableItems {
          deliverableItem {
            slug
            id
            ... on DeliverableItemInterface {
              slug
              id
            }
            ... on JerseyDeliverableItem {
              slug
              id
              signed
              competition {
                slug
                pictureUrl
              }
              team {
                ... on TeamInterface {
                  slug
                  name
                  pictureUrl
                }
              }
              ...JerseyRewardCard_JerseyDetails
            }
            ... on ExperienceDeliverableItem {
              slug
              id
              ...ExperienceRewardCard_DeliverableItem
            }
            ... on TicketsDeliverableItem {
              slug
              id
              ...TicketsRewardCard_DeliverableItem
            }
            ...DeliverableItemFooter_DeliverableItem
          }
        }
        ...formatRewardHeader_so5Reward
      }
      ${DeliverableItemFooter.fragments.deliverableItem}
      ${JerseyRewardCard.fragments.jersey}
      ${formatRewardHeader.fragments.so5Reward}
      ${ExperienceRewardCard.fragments.deliverableItem}
      ${TicketsRewardCard.fragments.deliverableItem}
    ` as TypedDocumentNode<formatDeliverableItemRewards_so5Reward>,
  }
);

export const formatReward = withFragments(
  (
    rewards: formatReward_so5Reward[],
    {
      sport,
      onClaimFiatRewards,
      safeGoNextReward,
      sendUserEngagementIncrementByTier,
    }: {
      sport?: Sport;
      onClaimFiatRewards?: () => void;
      safeGoNextReward?: () => void;
      sendUserEngagementIncrementByTier?: (tier: number) => void;
    } = {}
  ): Reward[] => {
    const deliverableReward = formatDeliverableItemRewards(
      rewards.filter(
        reward => reward.deliverableItems && reward.deliverableItems.length > 0
      ),
      { safeGoNextReward }
    );
    const coinReward = formatCoinRewards(
      rewards.filter(reward => reward.coinAmount > 0),
      { sport }
    );
    const monetaryRewards = formatMonetaryRewards(
      rewards.filter(reward => reward.amount),
      { onClaimFiatRewards }
    );
    const cardRewards = formatCardRewards(
      rewards.filter(reward => !!reward.rewardCards.length),
      { sport, sendUserEngagementIncrementByTier }
    );
    const boxRewards = formatBoxRewards(
      rewards.filter(reward => reward.probabilisticBundle)
    );
    const conversionCreditRewards = formatConversionCreditRewards(
      rewards.filter(reward => reward.conversionCredit)
    );

    return [
      ...(coinReward ? [coinReward] : []),
      ...monetaryRewards,
      ...cardRewards,
      ...boxRewards,
      ...conversionCreditRewards,
      ...deliverableReward,
    ];
  },
  {
    so5Reward: gql`
      fragment formatReward_so5Reward on So5Reward {
        slug
        id
        coinAmount
        amount {
          referenceCurrency
        }
        rewardCards {
          id
        }
        ...formatCoinRewards_so5Reward
        ...formatMonetaryRewards_so5Reward
        ...formatCardRewards_so5Reward
        ...formatBoxRewards_so5Reward
        ...formatConversionCreditRewards_so5Reward
        ...formatDeliverableItemRewards_so5Reward
      }
      ${formatCoinRewards.fragments.so5Reward}
      ${formatMonetaryRewards.fragments.so5Reward}
      ${formatCardRewards.fragments.so5Reward}
      ${formatBoxRewards.fragments.so5Reward}
      ${formatConversionCreditRewards.fragments.so5Reward}
      ${formatDeliverableItemRewards.fragments.so5Reward}
    ` as TypedDocumentNode<formatReward_so5Reward>,
  }
);
